import { zodResolver } from '@hookform/resolvers/zod';
import { formatPrice } from '@folio/lib';
import dayjs from 'dayjs';
import {
  type Invoice,
  manuallyMarkInvoiceInputBodyManualPaymentMethodEnum,
  useCustomerControllerManuallyMarkInvoice,
} from 'korint-sdk';
import { AlertTriangleIcon, CircleCheckIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Form } from '../../components/forms/form/form';
import { FormCheckboxInput } from '../../components/forms/form-checkbox-input';
import { FormDateInput } from '../../components/forms/form-date-input';
import { FormSelectInput } from '../../components/forms/form-select-input';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { ThemeIcon } from '@folio/components/ui/theme-icon';

type ManualMarkVariant = 'paid' | 'refunded';

/**
 * Mirrors the cheap backend eligibility rules of `manuallyMarkInvoice`
 * (customer.service.ts). The expensive ones (existing successful/processing
 * payments) are enforced backend-side only.
 */
const manualMarkVariant = (invoice: Invoice): ManualMarkVariant | null => {
  // Credit invoices belong to the replace-invoice workflow, which finalizes them itself
  if (invoice.creditedInvoiceId) {
    return null;
  }

  const amount = invoice.total.amountWithFeesAndTaxes;

  if (amount > 0 && (invoice.status === 'PAYMENT_FAILED' || invoice.status === 'ISSUED')) {
    return 'paid';
  }

  if (
    amount < 0 &&
    (invoice.status === 'REFUND_NOT_HANDLED' || invoice.status === 'PARTIALLY_FINALIZED')
  ) {
    return 'refunded';
  }

  return null;
};

const todayIso = () => dayjs().format('YYYY-MM-DD');

type ManuallyMarkInvoiceDialogProps = {
  customerId: string;
  invoice: Invoice;
  handleSuccess?(): Promise<void> | void;
};

export const ManuallyMarkInvoiceDialog = ({
  customerId,
  invoice,
  handleSuccess,
}: ManuallyMarkInvoiceDialogProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const variant = manualMarkVariant(invoice);

  const schema = z.object({
    manualPaymentMethod: z.nativeEnum(manuallyMarkInvoiceInputBodyManualPaymentMethodEnum, {
      required_error: t('invoices.module.paymentMethodRequired'),
    }),
    appliedAt: z
      .string({ required_error: t('invoices.manualMark.dateRequired') })
      .refine(date => date <= todayIso(), t('invoices.manualMark.dateInFuture')),
    acknowledged: z
      .boolean()
      .refine(acknowledged => acknowledged, t('invoices.manualMark.acknowledgementRequired')),
  });

  type ManualMarkFormValues = z.infer<typeof schema>;

  const form = useForm<ManualMarkFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { appliedAt: todayIso(), acknowledged: false },
  });

  const { mutateAsync: markInvoice } = useCustomerControllerManuallyMarkInvoice({
    mutation: {
      onSuccess: () => {
        handleSuccess?.();
        setOpen(false);
      },
    },
  });

  if (variant === null) {
    return null;
  }

  const onSubmit = async (values: ManualMarkFormValues) => {
    try {
      await markInvoice({
        customerId,
        data: {
          invoiceId: invoice.id,
          manualPaymentMethod: values.manualPaymentMethod,
          // 'YYYY-MM-DD' parses as UTC midnight, which east of UTC is still a few hours
          // ahead — clamp so "today" never trips the backend future-date rejection
          appliedAt: new Date(
            Math.min(new Date(values.appliedAt).getTime(), Date.now()),
          ).toISOString(),
        },
      });
    } catch {
      // The global MutationCache handler toasts API errors; the dialog stays open
      // so a failed marking is never mistaken for a success
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    // Reset on open, not close: the component mounts with the page, so a stale
    // "today" (or the leftovers of a failed submit) must never survive a reopen
    if (nextOpen) {
      form.reset({ appliedAt: todayIso(), acknowledged: false });
    }
  };

  const methodOptions = Object.values(manuallyMarkInvoiceInputBodyManualPaymentMethodEnum).map(
    value => ({
      value,
      label: t(`invoices.manualMark.method.${value}`),
    }),
  );

  return (
    <Form {...form}>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2 text-green-700 hover:text-green-700">
            <CircleCheckIcon className="h-4 w-4" />
            {t(`invoices.manualMark.action.${variant}`)}
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <Group align="start" gap={3}>
              <ThemeIcon tone="warning">
                <AlertTriangleIcon />
              </ThemeIcon>
              <Stack gap={0} className="gap-0.5">
                <DialogTitle>{t(`invoices.manualMark.title.${variant}`)}</DialogTitle>
                <DialogDescription>
                  {invoice.externalId || invoice.id} ·{' '}
                  {formatPrice(invoice.total.amountWithFeesAndTaxes)}
                </DialogDescription>
              </Stack>
            </Group>
          </DialogHeader>
          <Stack asChild>
            <form id="manually-mark-invoice-form" onSubmit={form.handleSubmit(onSubmit)}>
              <Text size="sm" tone="destructive" className="rounded-lg bg-destructive/5 px-4 py-3">
                <Text inline weight="semibold">
                  {t('invoices.manualMark.warningLead')}
                </Text>{' '}
                {t(`invoices.manualMark.warning.${variant}`)}
              </Text>
              <FormSelectInput
                label={t('invoices.module.paymentMethodLabel')}
                placeholder={t('invoices.module.paymentMethodPlaceholder')}
                name="manualPaymentMethod"
                required
                control={form.control}
                options={methodOptions}
              />
              <FormDateInput
                label={t(`invoices.manualMark.date.${variant}`)}
                name="appliedAt"
                required
                control={form.control}
                max={dayjs().format('YYYY-MM-DD')}
              />
              <FormCheckboxInput
                label={t('invoices.manualMark.acknowledgement')}
                name="acknowledged"
                control={form.control}
              />
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="outline">
                    {t('invoices.manualMark.cancel')}
                  </Button>
                </DialogClose>
                <Button
                  type="submit"
                  loading={form.formState.isSubmitting}
                  form="manually-mark-invoice-form"
                >
                  {t(`invoices.manualMark.confirm.${variant}`)}
                </Button>
              </DialogFooter>
            </form>
          </Stack>
        </DialogContent>
      </Dialog>
    </Form>
  );
};
